import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Github, ArrowRight, HardDrive, Search, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Logo } from '@/components/ui/logo'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { supabase, VITE_APP_URL } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'

export function LoginPage() {
  const { isAuthenticated, setLoading } = useAuthStore()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/'
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  useEffect(() => {
    setLoading(false)
  }, [setLoading])

  const handleGitHubLogin = async () => {
    setIsLoggingIn(true)
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: VITE_APP_URL,
        },
      })

      if (error) {
        console.error('Login error:', error)
        setLoading(false)
        setIsLoggingIn(false)
      }
      // Don't set loading to false on success, as we'll redirect
    } catch (error) {
      console.error('Login error:', error)
      setLoading(false)
      setIsLoggingIn(false)
    }
  }

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-background via-background to-secondary/20'>
      {/* Theme Toggle - Top Right Corner */}
      <div className='absolute top-4 right-4 z-10'>
        <ThemeToggle
          variant='ghost'
          size='icon'
          className='h-9 w-9 rounded-lg hover:bg-background/80 backdrop-blur-sm border border-border/50'
        />
      </div>

      <div className='min-h-screen flex items-center justify-center p-4'>
        <div className='max-w-4xl mx-auto grid lg:grid-cols-2 gap-12 items-center'>
          {/* Left side - Value proposition */}
          <div className='space-y-8'>
            <div className='space-y-4'>
              <h1 className='text-4xl lg:text-5xl font-bold tracking-tight'>
                AI-Native File Storage
                <span className='block text-primary'>for Developers</span>
              </h1>
              <p className='text-xl text-muted-foreground'>
                Replace your Frankenstein stack with OpenArtifacts.
              </p>
            </div>

            {/* Feature highlights - Minimal with icons */}
            <div className='space-y-4'>
              <div className='flex items-center space-x-3 group'>
                <div className='w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all group-hover:bg-primary/20'>
                  <HardDrive className='w-5 h-5 text-primary' />
                </div>
                <span className='text-lg text-foreground/80 group-hover:text-foreground transition-colors'>
                  AI native file operations
                </span>
              </div>
              <div className='flex items-center space-x-3 group'>
                <div className='w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all group-hover:bg-primary/20'>
                  <Search className='w-5 h-5 text-primary' />
                </div>
                <span className='text-lg text-foreground/80 group-hover:text-foreground transition-colors'>
                  Semantic search
                </span>
              </div>
              <div className='flex items-center space-x-3 group'>
                <div className='w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all group-hover:bg-primary/20'>
                  <Zap className='w-5 h-5 text-primary' />
                </div>
                <span className='text-lg text-foreground/80 group-hover:text-foreground transition-colors'>
                  Real-time sync
                </span>
              </div>
            </div>
          </div>

          {/* Right side - Login form */}
          <div className='flex justify-center lg:justify-end'>
            <Card className='w-full max-w-md border-2 shadow-xl hover:shadow-2xl transition-shadow duration-300'>
              <CardHeader className='text-center space-y-4 pb-6'>
                <div className='flex justify-center'>
                  <Logo size='lg' />
                </div>
                <div className='space-y-2'>
                  <CardTitle className='text-2xl font-bold'>Get Started</CardTitle>
                  <CardDescription className='text-base'>
                    Sign in with GitHub to access your AI file management dashboard
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className='space-y-6'>
                <Button
                  onClick={handleGitHubLogin}
                  disabled={isLoggingIn}
                  className='w-full h-12 text-base'
                  size='lg'
                >
                  {isLoggingIn ? (
                    <>
                      <LoadingSpinner size='sm' className='mr-2' />
                      Connecting to GitHub...
                    </>
                  ) : (
                    <>
                      <Github className='w-5 h-5 mr-2' />
                      Continue with GitHub
                      <ArrowRight className='w-4 h-4 ml-2' />
                    </>
                  )}
                </Button>

                <div className='space-y-3'>
                  <div className='relative'>
                    <div className='absolute inset-0 flex items-center'>
                      <div className='w-full border-t border-border' />
                    </div>
                    <div className='relative flex justify-center text-xs'>
                      <span className='bg-card px-2 text-muted-foreground'>What you get</span>
                    </div>
                  </div>

                  <div className='space-y-2 text-sm text-muted-foreground mx-auto max-w-xs'>
                    <div className='flex items-center space-x-2'>
                      <div className='w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0' />
                      <span>AI-native file operations</span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <div className='w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0' />
                      <span>Support for OpenAI, Anthropic & more</span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <div className='w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0' />
                      <span>Track files, API usage & more</span>
                    </div>
                  </div>
                </div>

                <div className='text-center text-xs text-muted-foreground'>
                  <p>Free for developers • No credit card required</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
